import React, { useState, useEffect } from 'react';
import { 
  Users, TrendingUp, Clock, Calendar, MessageSquare,
  Award, Medal, Trophy, Target
} from 'lucide-react';
import { 
  getAttendantRanking, getTenantPeriodReport,
  getConversionByType
} from '../services/attendanceApi';
import { useLanguage } from '../contexts/LanguageContext';

function TeamPerformance() {
  const { t } = useLanguage();
  
  const [ranking, setRanking] = useState([]);
  const [periodReport, setPeriodReport] = useState(null);
  const [conversionByType, setConversionByType] = useState([]);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState('30days');

  useEffect(() => {
    loadData();
  }, [period]);

  const loadData = async () => {
    setLoading(true);
    try {
      const endDate = new Date().toISOString().split('T')[0];
      let startDate;
      
      if (period === '7days') {
        startDate = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
      } else if (period === '30days') {
        startDate = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
      } else {
        startDate = new Date(Date.now() - 90 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
      }

      const [rankingRes, periodRes, conversionRes] = await Promise.all([
        getAttendantRanking(startDate, endDate),
        getTenantPeriodReport(startDate, endDate),
        getConversionByType(startDate, endDate)
      ]);

      setRanking(rankingRes.data);
      setPeriodReport(periodRes.data);
      setConversionByType(conversionRes.data);
    } catch (err) {
      console.error('Erro ao carregar dados:', err);
    } finally {
      setLoading(false);
    }
  };

  const getMedalIcon = (index) => {
    if (index === 0) return <Trophy size={24} className="text-yellow-500" />;
    if (index === 1) return <Medal size={24} className="text-gray-400" />;
    if (index === 2) return <Medal size={24} className="text-orange-600" />;
    return <span className="text-gray-500 font-bold">#{index + 1}</span>;
  };

  const summary = periodReport?.summary || {};

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-slate-100 flex items-center gap-2">
            <Users size={28} className="text-blue-600 dark:text-blue-400" />
            {t('teamPerformance.title')}
          </h1>
          <p className="text-gray-500 dark:text-slate-400">{t('teamPerformance.subtitle')}</p>
        </div>
        <select
          value={period}
          onChange={e => setPeriod(e.target.value)}
          className="px-4 py-2 bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-xl text-sm focus:ring-2 focus:ring-blue-500 outline-none text-gray-900 dark:text-slate-100"
        >
          <option value="7days">{t('teamPerformance.last7days')}</option>
          <option value="30days">{t('teamPerformance.last30days')}</option>
          <option value="90days">{t('teamPerformance.last90days')}</option>
        </select>
      </div>

      {/* Resumo Geral */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-gradient-to-br from-blue-500 to-blue-600 p-5 rounded-2xl text-white shadow-lg">
          <Users size={24} className="mb-2 opacity-80" />
          <p className="text-3xl font-bold">{summary.total_attendants || 0}</p>
          <p className="text-sm opacity-90">{t('teamPerformance.activeAttendants')}</p>
        </div>

        <div className="bg-gradient-to-br from-green-500 to-green-600 p-5 rounded-2xl text-white shadow-lg">
          <MessageSquare size={24} className="mb-2 opacity-80" />
          <p className="text-3xl font-bold">{summary.total_conversations || 0}</p>
          <p className="text-sm opacity-90">{t('teamPerformance.totalConversations')}</p>
        </div>

        <div className="bg-gradient-to-br from-purple-500 to-purple-600 p-5 rounded-2xl text-white shadow-lg">
          <Calendar size={24} className="mb-2 opacity-80" />
          <p className="text-3xl font-bold">{summary.total_appointments || 0}</p>
          <p className="text-sm opacity-90">{t('teamPerformance.appointments')}</p>
        </div>

        <div className="bg-gradient-to-br from-orange-500 to-orange-600 p-5 rounded-2xl text-white shadow-lg">
          <Clock size={24} className="mb-2 opacity-80" />
          <p className="text-3xl font-bold">{summary.avg_response_minutes || 0}m</p>
          <p className="text-sm opacity-90">{t('teamPerformance.avgResponseTime')}</p>
        </div>
      </div>

      {/* Ranking dos Atendentes */}
      <div className="bg-white dark:bg-slate-800 rounded-2xl border border-gray-100 dark:border-slate-700 shadow-sm overflow-hidden">
        <div className="p-5 border-b border-gray-100 dark:border-slate-700">
          <h2 className="text-lg font-bold text-gray-900 dark:text-slate-100 flex items-center gap-2">
            <Trophy size={20} className="text-yellow-500" />
            {t('teamPerformance.ranking')}
          </h2>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-left">
            <thead className="bg-gray-50 dark:bg-slate-900 text-gray-500 dark:text-slate-400 text-xs uppercase">
              <tr>
                <th className="px-6 py-3 font-semibold">{t('teamPerformance.position')}</th>
                <th className="px-6 py-3 font-semibold">{t('teamPerformance.attendant')}</th>
                <th className="px-6 py-3 font-semibold">{t('teamPerformance.conversations')}</th>
                <th className="px-6 py-3 font-semibold">{t('teamPerformance.appointments')}</th>
                <th className="px-6 py-3 font-semibold">{t('teamPerformance.responseTime')}</th>
                <th className="px-6 py-3 font-semibold">{t('teamPerformance.conversion')}</th>
                <th className="px-6 py-3 font-semibold">{t('teamPerformance.satisfaction')}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-slate-700">
              {loading ? (
                <tr>
                  <td colSpan={7} className="px-6 py-12 text-center text-gray-400 dark:text-slate-500">
                    {t('common.loading')}
                  </td>
                </tr>
              ) : ranking.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-6 py-12 text-center text-gray-400 dark:text-slate-500">
                    {t('teamPerformance.noData')}
                  </td>
                </tr>
              ) : (
                ranking.map((user, idx) => (
                  <tr key={user.user_id} className={`hover:bg-gray-50 dark:hover:bg-slate-700 ${idx < 3 ? 'bg-gradient-to-r from-yellow-50/30 to-transparent dark:from-yellow-900/10' : ''}`}>
                    <td className="px-6 py-4">
                      <div className="flex items-center justify-center w-10">
                        {getMedalIcon(idx)}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-gradient-to-br from-blue-400 to-blue-600 text-white rounded-full flex items-center justify-center font-bold">
                          {user.user_name?.charAt(0).toUpperCase()}
                        </div>
                        <div>
                          <p className="font-semibold text-gray-900 dark:text-slate-100">{user.user_name}</p>
                          <p className="text-xs text-gray-500 dark:text-slate-400">{user.email}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 font-bold text-gray-900 dark:text-slate-100">
                      {user.total_conversations || 0}
                    </td>
                    <td className="px-6 py-4">
                      <span className="px-2 py-1 bg-purple-50 dark:bg-purple-900/30 text-purple-700 dark:text-purple-400 rounded-full text-xs font-bold">
                        {user.total_appointments || 0}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-600 dark:text-slate-400">
                      {user.avg_response_minutes || 0} {t('teamPerformance.min')}
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        <div className="w-16 bg-gray-200 dark:bg-slate-700 rounded-full h-2">
                          <div 
                            className="bg-green-600 h-2 rounded-full"
                            style={{ width: `${Math.min(user.conversion_rate || 0, 100)}%` }}
                          />
                        </div>
                        <span className="text-xs font-medium text-gray-600 dark:text-slate-400">
                          {user.conversion_rate || 0}%
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-1">
                        {[1, 2, 3, 4, 5].map(star => (
                          <span 
                            key={star} 
                            className={`text-lg ${
                              star <= (user.avg_satisfaction || 0) 
                                ? 'text-yellow-400' 
                                : 'text-gray-300 dark:text-slate-600'
                            }`}
                          >
                            ★
                          </span>
                        ))}
                        <span className="text-xs text-gray-500 dark:text-slate-400 ml-1">
                          {user.avg_satisfaction?.toFixed(1) || '—'}
                        </span>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Conversão por Tipo */}
      <div className="bg-white dark:bg-slate-800 rounded-2xl border border-gray-100 dark:border-slate-700 shadow-sm overflow-hidden">
        <div className="p-5 border-b border-gray-100 dark:border-slate-700">
          <h2 className="text-lg font-bold text-gray-900 dark:text-slate-100 flex items-center gap-2">
            <Target size={20} className="text-green-600 dark:text-green-400" />
            {t('teamPerformance.conversionByType')}
          </h2>
        </div>
        <div className="p-5 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {conversionByType.length === 0 ? (
            <p className="text-gray-400 dark:text-slate-500 text-sm col-span-full text-center py-8">
              {t('teamPerformance.noData')}
            </p>
          ) : (
            conversionByType.map((type, idx) => (
              <div key={idx} className="bg-gradient-to-br from-gray-50 to-white dark:from-slate-700 dark:to-slate-800 border border-gray-200 dark:border-slate-600 rounded-xl p-4">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-sm font-bold text-gray-900 dark:text-slate-100 uppercase">
                    {type.conversation_type}
                  </span>
                  <span className="text-xs px-2 py-1 bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400 rounded-full font-bold">
                    {type.total_conversations} {t('teamPerformance.conv')}
                  </span>
                </div>
                <div className="space-y-2">
                  <div className="flex justify-between text-xs">
                    <span className="text-gray-500 dark:text-slate-400">{t('teamPerformance.appointments')}</span>
                    <span className="font-bold text-gray-900 dark:text-slate-100">{type.total_appointments}</span>
                  </div>
                  <div className="flex justify-between text-xs">
                    <span className="text-gray-500 dark:text-slate-400">{t('teamPerformance.conversionRate')}</span>
                    <span className="font-bold text-green-600 dark:text-green-400">{type.conversion_rate}%</span>
                  </div>
                  <div className="flex justify-between text-xs">
                    <span className="text-gray-500 dark:text-slate-400">{t('teamPerformance.responseTime')}</span>
                    <span className="font-bold text-gray-900 dark:text-slate-100">{type.avg_response_minutes} {t('teamPerformance.min')}</span>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}

export default TeamPerformance;